import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper, Typography, Button, IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(() => ({
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(17, 24, 39, 0.52)',
    zIndex: 2000,
  },
  highlight: {
    position: 'fixed',
    borderRadius: 12,
    border: '2px solid #60A5FA',
    boxShadow: '0 0 0 9999px rgba(17, 24, 39, 0.52)',
    zIndex: 2001,
    pointerEvents: 'none',
    transition: 'all 0.18s ease',
  },
  card: {
    position: 'fixed',
    zIndex: 2002,
    width: 320,
    maxWidth: 'calc(100vw - 24px)',
    borderRadius: 14,
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.28)',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '16px 16px 10px',
  },
  stepMeta: {
    fontSize: 11,
    fontWeight: 700,
    color: '#4F46E5',
    textTransform: 'uppercase',
    letterSpacing: 0.45,
    marginBottom: 6,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
    lineHeight: 1.25,
  },
  closeBtn: {
    marginTop: -4,
    marginRight: -4,
  },
  body: {
    padding: '0 16px 16px',
    fontSize: 13,
    lineHeight: 1.5,
    color: '#4B5563',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '12px 16px 16px',
    borderTop: '1px solid #E5E7EB',
  },
  footerLeft: {
    display: 'flex',
    gap: 8,
  },
  footerRight: {
    display: 'flex',
    gap: 8,
  },
  ghostBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  primaryBtn: {
    textTransform: 'none',
    fontWeight: 600,
  },
}));

const EDGE_PADDING = 12;
const HIGHLIGHT_PADDING = 8;
const TARGET_RESOLVE_ATTEMPTS = 12;
const TARGET_RESOLVE_INTERVAL_MS = 120;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getPlacementPosition = (rect, cardRect, placement) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const gap = 14;
  const cardWidth = cardRect?.width || 320;
  const cardHeight = cardRect?.height || 220;

  if (!rect) {
    return {
      left: clamp((viewportWidth - cardWidth) / 2, EDGE_PADDING, viewportWidth - cardWidth - EDGE_PADDING),
      top: clamp((viewportHeight - cardHeight) / 2, EDGE_PADDING, viewportHeight - cardHeight - EDGE_PADDING),
    };
  }

  const centerX = rect.left + (rect.width / 2);
  const centerY = rect.top + (rect.height / 2);
  let left = centerX - (cardWidth / 2);
  let top = rect.bottom + gap;

  if (placement === 'left') {
    left = rect.left - cardWidth - gap;
    top = centerY - (cardHeight / 2);
  } else if (placement === 'right') {
    left = rect.right + gap;
    top = centerY - (cardHeight / 2);
  } else if (placement === 'top') {
    left = centerX - (cardWidth / 2);
    top = rect.top - cardHeight - gap;
  }

  return {
    left: clamp(left, EDGE_PADDING, viewportWidth - cardWidth - EDGE_PADDING),
    top: clamp(top, EDGE_PADDING, viewportHeight - cardHeight - EDGE_PADDING),
  };
};

const SiteTakeoffTour = ({
  open,
  step,
  stepIndex,
  totalSteps,
  onNext,
  onBack,
  onClose,
  onTargetMissing,
}) => {
  const classes = useStyles();
  const cardRef = useRef(null);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    if (!open || !step?.target) {
      setTargetRect(null);
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const syncTarget = () => {
      const element = document.querySelector(step.target);
      if (!element) return false;
      element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      setTargetRect(element.getBoundingClientRect());
      return true;
    };

    const interval = window.setInterval(() => {
      if (cancelled) return;
      attempts += 1;
      if (syncTarget()) {
        window.clearInterval(interval);
      } else if (attempts >= TARGET_RESOLVE_ATTEMPTS) {
        window.clearInterval(interval);
        setTargetRect(null);
        onTargetMissing?.();
      }
    }, TARGET_RESOLVE_INTERVAL_MS);

    const resync = () => {
      const element = document.querySelector(step.target);
      if (!element) return;
      setTargetRect(element.getBoundingClientRect());
    };

    window.addEventListener('resize', resync);
    window.addEventListener('scroll', resync, true);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('resize', resync);
      window.removeEventListener('scroll', resync, true);
    };
  }, [open, step, onTargetMissing]);

  const highlightStyle = useMemo(() => {
    if (!targetRect) return null;
    return {
      top: Math.max(targetRect.top - HIGHLIGHT_PADDING, EDGE_PADDING),
      left: Math.max(targetRect.left - HIGHLIGHT_PADDING, EDGE_PADDING),
      width: targetRect.width + (HIGHLIGHT_PADDING * 2),
      height: targetRect.height + (HIGHLIGHT_PADDING * 2),
    };
  }, [targetRect]);

  const cardPosition = useMemo(() => (
    getPlacementPosition(targetRect, cardRef.current?.getBoundingClientRect(), step?.placement)
  ), [targetRect, step]);

  if (!open || !step) return null;

  return (
    <>
      <div className={classes.backdrop} />
      {highlightStyle && <div className={classes.highlight} style={highlightStyle} />}

      <Paper ref={cardRef} className={classes.card} style={cardPosition}>
        <div className={classes.cardHeader}>
          <div>
            <div className={classes.stepMeta}>
              Step {stepIndex + 1} of {totalSteps}
            </div>
            <Typography className={classes.title}>{step.title}</Typography>
          </div>
          <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <Typography component="div" className={classes.body}>
          {step.body}
        </Typography>

        <div className={classes.footer}>
          <div className={classes.footerLeft}>
            <Button size="small" onClick={onClose} className={classes.ghostBtn}>
              End tour
            </Button>
          </div>
          <div className={classes.footerRight}>
            <Button
              size="small"
              onClick={onBack}
              disabled={stepIndex === 0}
              className={classes.ghostBtn}
            >
              Back
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={onNext}
              className={classes.primaryBtn}
            >
              {stepIndex === totalSteps - 1 ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </Paper>
    </>
  );
};

export default SiteTakeoffTour;
